import { getCustomerById } from '../config/customers';
import { environment, getResolvedCustomerId } from '../config/environment';
import { getPortalSelectUrl as buildPortalSelectUrl, isLocalhost, redirectTo } from './runtimeUrls';

const SELECTED_CUSTOMER_KEY = 'opencare.selectedCustomerId';
const CUSTOMER_URL_PARAM = 'k';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name) {
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!match) return null;
  return decodeURIComponent(match.slice(prefix.length));
}

function writeCookie(name, value) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain =
    !isLocalhost() && environment.cookieDomain
      ? `; domain=${environment.cookieDomain}`
      : '';

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}${domain}`;
}

function clearCookie(name) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain =
    !isLocalhost() && environment.cookieDomain
      ? `; domain=${environment.cookieDomain}`
      : '';

  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}${domain}`;
}

function readStoredCustomerId() {
  const cookieValue = readCookie(SELECTED_CUSTOMER_KEY);
  if (cookieValue) {
    return cookieValue;
  }

  return localStorage.getItem(SELECTED_CUSTOMER_KEY);
}

export function getSelectedCustomerId() {
  return readStoredCustomerId();
}

export function setSelectedCustomerId(customerId) {
  localStorage.setItem(SELECTED_CUSTOMER_KEY, customerId);
  writeCookie(SELECTED_CUSTOMER_KEY, customerId);
}

export function clearSelectedCustomerId() {
  localStorage.removeItem(SELECTED_CUSTOMER_KEY);
  clearCookie(SELECTED_CUSTOMER_KEY);
}

export function getSelectedCustomer() {
  return getCustomerById(getSelectedCustomerId());
}

export function getPortalSelectUrl() {
  return buildPortalSelectUrl();
}

/** Read ?k= from URL after portal redirect (needed because localStorage is per port on localhost). */
export function syncSelectedCustomerFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get(CUSTOMER_URL_PARAM);
  if (!fromUrl) {
    return null;
  }

  const customer = getCustomerById(fromUrl);
  if (!customer) {
    return null;
  }

  setSelectedCustomerId(customer.id);

  params.delete(CUSTOMER_URL_PARAM);
  const search = params.toString();
  const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', nextUrl);

  return customer.id;
}

export function ensureCustomerSelection({ redirectIfMissing = false } = {}) {
  syncSelectedCustomerFromUrl();

  if (getSelectedCustomerId()) {
    return getSelectedCustomer();
  }

  // Prefer env/hostname customer (e.g. REACT_APP_CUSTOMER_ID=demo) before
  // redirecting to the portal — local portal on :3000 is often another app.
  const resolvedId = getResolvedCustomerId();
  if (resolvedId) {
    setSelectedCustomerId(resolvedId);
    return getSelectedCustomer();
  }

  if (redirectIfMissing) {
    redirectTo(buildPortalSelectUrl());
  }

  return null;
}

export function getCustomerUrlParam() {
  return CUSTOMER_URL_PARAM;
}
